import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardActionArea, Paper, Stack } from "@mui/material";
import {
  iconSizeByAvatarSize,
  Text,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import { formatDistanceToNow } from "date-fns";

import { useWorkerProfilePicture } from "../Agents/api/useWorkerProfilePicture";
import { useWorkplacePhotos } from "../Facilities/api/useWorkplacePhotos";
import { APP_V2_USER_EVENTS, logEvent } from "../lib/analytics";
import { useGetQualificationDisplayLabels } from "../Qualifications/api/useGetQualificationDisplayLabels";
import { WorkerAvatar } from "../redesign/components/WorkerAvatar";
import { WorkplaceAvatar } from "../redesign/components/WorkplaceAvatar";
import { useDefinedWorker } from "../Worker/useDefinedWorker";
import { NewsFeedItemCommentsCount } from "./CommentsCount";
import { NewsFeedItemContentSheet } from "./ContentSheet";
import { NewsFeedItemReactionsCount } from "./ReactionsCount";
import {
  type NewsFeedItem,
  type NewsFeedItemIncludedShift,
  type NewsFeedItemIncludedWorker,
  type NewsFeedItemIncludedWorkplace,
  NewsFeedItemRecommendationEvent,
  NewsFeedItemType,
} from "./types";
import { getNewsFeedContentText } from "./utils/getNewsFeedContentText";

interface NewsFeedItemContentCardProps {
  newsFeedItem: NewsFeedItem;
  included: {
    includedShift?: NewsFeedItemIncludedShift;
    includedWorkplace?: NewsFeedItemIncludedWorkplace;
    includedRecommendedWorkplace?: NewsFeedItemIncludedWorkplace;
    includedWorker?: NewsFeedItemIncludedWorker;
    includedRecommendedWorker?: NewsFeedItemIncludedWorker;
    includedReferredWorker?: NewsFeedItemIncludedWorker;
  };
}

export default function NewsFeedItemContentCard(props: NewsFeedItemContentCardProps) {
  const { newsFeedItem, included } = props;

  const { data: workerPicture, isInitialLoading: isLoadingWorkerPicture } = useWorkerProfilePicture(
    included.includedRecommendedWorker?.id ??
      (newsFeedItem.attributes.type === NewsFeedItemType.POST
        ? newsFeedItem.attributes.createdBy
        : newsFeedItem.attributes.recommendedBy),
    {
      useErrorBoundary: false,
      retry: 0,
      enabled:
        newsFeedItem.attributes.metadata.event !==
        NewsFeedItemRecommendationEvent.WORKPLACE_RECOMMENDATION,
    }
  );

  const { data: workplacePhotos, isLoading: isLoadingWorkplacePhotos } = useWorkplacePhotos(
    { workplaceId: included.includedRecommendedWorkplace?.id ?? "" },
    {
      useErrorBoundary: false,
      retry: 0,
      enabled:
        isDefined(included.includedRecommendedWorkplace?.id) &&
        newsFeedItem.attributes.metadata.event ===
          NewsFeedItemRecommendationEvent.WORKPLACE_RECOMMENDATION,
    }
  );

  const workplacePhoto = workplacePhotos?.photosList?.[0];

  const modalState = useModalState();
  const currentWorker = useDefinedWorker();
  const includedShiftQualification = included.includedShift?.attributes.qualification ?? "";

  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([
    includedShiftQualification,
  ]);

  const shiftQualificationDisplayLabel =
    qualificationNameToDisplayLabelMap.get(includedShiftQualification) ??
    includedShiftQualification;

  return (
    <Paper variant="tertiary" elevation={2}>
      <CardActionArea
        onClick={() => {
          logEvent(APP_V2_USER_EVENTS.NEWS_FEED_CARD_CLICKED, {
            event: newsFeedItem.attributes.metadata.event,
            metadata: newsFeedItem.attributes.metadata,
          });
          modalState.openModal();
        }}
      >
        <Stack direction="row" spacing={3} p={2} alignItems="center" height="100%">
          <Box sx={{ width: iconSizeByAvatarSize.medium }}>
            {newsFeedItem.attributes.metadata.event ===
            NewsFeedItemRecommendationEvent.WORKPLACE_RECOMMENDATION ? (
              <WorkplaceAvatar
                size="medium"
                isLoading={isLoadingWorkplacePhotos}
                pictureUrl={workplacePhoto?.url}
              />
            ) : (
              <WorkerAvatar
                profilePictureUrl={workerPicture?.distribution_url}
                isLoading={isLoadingWorkerPicture}
              />
            )}
          </Box>
          <Stack justifyContent="space-between" p={1} height="100%" spacing={1}>
            <Text>
              {getNewsFeedContentText({
                event: newsFeedItem.attributes.metadata.event,
                currentWorkerName: currentWorker.name,

                workerName: included.includedWorker?.attributes.name,
                referredWorkerName: included.includedReferredWorker?.attributes.name,
                recommendedWorkerName: included.includedRecommendedWorker?.attributes.name,

                workplaceName: included.includedWorkplace?.attributes.name,
                recommendedWorkplaceName: included.includedRecommendedWorkplace?.attributes.name,

                shiftQualificationDisplayLabel,
              })}
            </Text>
            <Stack direction="row" justifyContent="space-between">
              <Text variant="caption" color={(theme) => theme.palette.text.tertiary}>
                {formatDistanceToNow(new Date(newsFeedItem.attributes.createdAt))} ago
              </Text>
              {newsFeedItem.attributes.type === NewsFeedItemType.POST && (
                <Stack direction="row" spacing={4}>
                  <NewsFeedItemReactionsCount
                    showIcon
                    count={newsFeedItem.attributes.reactions.length}
                  />
                  <NewsFeedItemCommentsCount
                    showIcon
                    count={newsFeedItem.attributes.comments.length}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      </CardActionArea>
      <NewsFeedItemContentSheet
        modalState={modalState}
        newsFeedItem={newsFeedItem}
        isLoadingWorkerPicture={isLoadingWorkerPicture}
        workerPictureUrl={workerPicture?.distribution_url}
        isLoadingWorkplacePicture={isLoadingWorkplacePhotos}
        workplacePictureUrl={workplacePhoto?.url}
        included={included}
      />
    </Paper>
  );
}
